import React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Text from "@/components/ui/text";
import Badge from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Resolve } from "@dreamhub/designsystem/icons";
import { useUsers } from "@/entities/users/hooks";
import { simpleDate } from "@/utils/datetime";
import { useAssignOnboardingWorkflowToStage } from "@/pages/settings/components/contractsSettings/hooks";
import { OnboardingStageWorkflow } from "@/pages/settings/components/contractsSettings/types";
import {
  getOnboardingWorkflowBadgeClass,
  getOnboardingWorkflowIcon,
  getOnboardingWorkflowTypeLabel
} from "./utils";

interface OnboardingWorkflowCardProps {
  workflow: OnboardingStageWorkflow;
  currentStageId?: string;
  onAssigned?: () => void;
}

const OnboardingWorkflowCard: React.FC<OnboardingWorkflowCardProps> = ({
  workflow,
  currentStageId,
  onAssigned
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const assignWorkflow = useAssignOnboardingWorkflowToStage();

  const {
    id,
    name,
    description,
    workflowType,
    contractsOnboardingStageId,
    stageName,
    lastModifiedAt,
    lastModifiedBy
  } = workflow;

  const { users } = useUsers(lastModifiedBy ? [lastModifiedBy] : [], {
    enabled: !!lastModifiedBy
  });

  const editedByText = (() => {
    if (!lastModifiedBy || !lastModifiedAt) return "";
    const user = users?.find((u) => u.id === lastModifiedBy);
    const userName = user?.displayName || "";
    const formattedDate = simpleDate(new Date(lastModifiedAt), "short", false);
    return t("workflows.status.editedBy", { userName, date: formattedDate });
  })();

  const isInUse = !!contractsOnboardingStageId;

  const handleAddToStage = async () => {
    if (!currentStageId) return;
    try {
      await assignWorkflow.mutateAsync({
        pathParams: { workflowId: id },
        data: { contractsOnboardingStageId: currentStageId }
      });
      queryClient.invalidateQueries({
        queryKey: ["settings/contracts/stages/stageById"]
      });
      queryClient.invalidateQueries({
        queryKey: ["settings/contracts/stages/workflows/all"]
      });
      onAssigned?.();
    } catch (error) {
      console.error("Failed to assign onboarding workflow to stage:", error);
      toast.error(t("workflows.modal.assignError"));
    }
  };

  return (
    <div className="flex h-[152px] p-4 justify-center items-center gap-4 rounded-2xl border border-[#2B374A] bg-[#1C2432]">
      <div className="flex-shrink-0">{getOnboardingWorkflowIcon(workflowType)}</div>
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-2 h-[68px]">
          <Text variant="lgLabel">{name}</Text>
          {description ? (
            <Text variant="bodySecondary">{description}</Text>
          ) : null}
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row items-center gap-2">
            <Badge
              text={getOnboardingWorkflowTypeLabel(workflowType, t)}
              textVariant="body"
              className={getOnboardingWorkflowBadgeClass(workflowType)}
            />
            {stageName ? (
              <Badge
                text={`${t("usedIn")} ${stageName}`}
                textVariant="body"
                textClassName="text-primary"
                className="bg-badges-filled border-border-badge-selected px-[6px] py-1"
                icon={<Resolve />}
              />
            ) : null}
            {editedByText ? (
              <Text variant="smBodyTertiary">{editedByText}</Text>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {isInUse ? (
              <Button variant="secondary" disabled>
                {t("inUse")}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleAddToStage}
                disabled={!currentStageId || assignWorkflow.isPending}
              >
                {t("addToStage")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWorkflowCard;
